
import { useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { Message } from "@/types/chat";
import { generateFallbackResponse } from "@/utils/chatUtils";
import { sendChatRequest, ChatContextData } from "@/lib/chatService";
import { useStore } from "@/store/useStore";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useAuth } from "@/hooks/useAuth";
import { useUserId } from "./useUserId";
import { getCityFromCoordinatesWithCache } from "@/utils/geocoding";

interface MessageHandlingProps {
  addMessage: (message: Message) => void;
  setIsLoading: (loading: boolean) => void;
  scrollAreaRef: React.RefObject<HTMLDivElement>;
}

export function useMessageHandling({
  addMessage,
  setIsLoading,
  scrollAreaRef
}: MessageHandlingProps) {
  const { toast } = useToast();
  const { 
    currentGoldPrice, 
    currentSilverPrice, 
    dealers 
  } = useStore();
  
  // Hook per geolocalizzazione e autenticazione
  const { coordinates, position } = useGeolocation();
  const { user } = useAuth();
  const { userId, isAnonymous } = useUserId();

  // Funzione per gestire la chiamata al webhook
  const getWebhookResponse = async (question: string): Promise<string> => {
    try {
      // Otteniamo la città dalle coordinate se disponibili
      let userCity = null;
      if (coordinates) {
        try {
          const geocodeResult = await getCityFromCoordinatesWithCache(
            coordinates.latitude, 
            coordinates.longitude
          );
          userCity = geocodeResult.city || null;
        } catch (error) {
          console.warn("Errore nella geocodifica:", error);
        }
      }

      // Prepariamo i dati completi per il contesto che verranno inviati al webhook
      const contextData: ChatContextData = {
        message: question,
        timestamp: new Date().toISOString(),
        goldPrice: currentGoldPrice?.price || null,
        goldChange: currentGoldPrice?.change || null,
        goldPriceDate: currentGoldPrice?.timestamp || null,
        silverPrice: currentSilverPrice?.price || null,
        silverChange: currentSilverPrice?.change || null,
        silverPriceDate: currentSilverPrice?.timestamp || null,
        dealersCount: dealers?.length || 0,
        topDealers: dealers?.slice(0, 3).map(d => d.name) || [],
        userLocation: coordinates ? {
          latitude: coordinates.latitude,
          longitude: coordinates.longitude
        } : null,
        userCity: userCity,
        userName: user?.email?.split('@')[0] || null,
        userEmail: user?.email || null,
        userId: userId,
        isAnonymous: isAnonymous
      };
      
      console.log("Inviando al webhook:", contextData);
      
      // Inviamo la richiesta al servizio chat
      const response = await sendChatRequest(contextData);
      
      console.log("Risposta completa dal webhook:", response);
      
      // Se abbiamo una risposta valida dal webhook, la usiamo
      if (response && response.text) {
        console.log("Risposta di testo dal webhook:", response.text);
        return response.text;
      }
      
      // Altrimenti usiamo la risposta di fallback
      console.log("Generando risposta di fallback...");
      return generateFallbackResponse(question, {
        currentGoldPrice,
        currentSilverPrice,
        dealers
      });
    } catch (error) {
      console.error("Errore generale nel getWebhookResponse:", error);
      return generateFallbackResponse(question, {
        currentGoldPrice,
        currentSilverPrice,
        dealers
      });
    }
  };

  const handleSendMessage = useCallback(async (userMessage: string) => {
    setIsLoading(true);
    
    // Aggiungi il messaggio dell'utente
    const userMsg: Message = {
      id: `user-${Date.now()}`,
      text: userMessage,
      isUser: true,
      timestamp: new Date()
    };
    addMessage(userMsg);

    // Scroll automatico
    setTimeout(() => {
      if (scrollAreaRef.current) {
        scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
      }
    }, 100);
    
    try {
      // Otteniamo la risposta dal webhook
      const webhookResponse = await getWebhookResponse(userMessage);
      
      // Creiamo la risposta del bot
      const botResponse: Message = {
        id: `bot-${Date.now()}`,
        text: webhookResponse,
        isUser: false,
        timestamp: new Date()
      };
      
      addMessage(botResponse);

      // Scroll automatico dopo la risposta
      setTimeout(() => {
        if (scrollAreaRef.current) {
          scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error("Errore invio messaggio:", error);
      toast({
        title: "Errore",
        description: "Non è stato possibile inviare il messaggio. Riprova più tardi.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [addMessage, setIsLoading, scrollAreaRef, toast, currentGoldPrice, currentSilverPrice, dealers]);

  return {
    handleSendMessage,
    getWebhookResponse
  };
}
